import React from 'react';
import { Messages, Control, API, Code, Name, Title, Actions, Form, Hooks, Files, Toast, Button, useListContext, useCallback, useEffect, useState, Utility } from 'helpers/ListImports';
import { Billing } from 'models/BillingModel';
import { ControlSession } from 'components/common/ControlSession';
import CustomTooltip from 'components/common/CustomTooltip';

const BillingList: React.FC = () => {
  const width = { sno: 8, client: 16, amount: 8, paid: 8, hsc:6, diag:6, status:9, doctor:12,  billdate: 9, claimdate:9, remitdate:9 }
  const [formId, formName] = [Code.Form.Id.BILLING, Code.Form.Name.BILLING];
  const { loading, setLoading, error, setError, search, setSearch, pno, setPno, pageRows, setPageRow, activeId, setActiveId, selected, setSelected, confirm, setConfirm, message, setMessage,  showMenu, setShowMenu, selectedItems, setSelectedItems, selectAllChecked, setSelectAllChecked, activeOptions, setActiveOptions, rowOptions, setRowOptions, menuPosition, setMenuPosition, iframeRef, filterRef, contextMenuRef  } = useListContext();
  const [billingData, setBillingData] = useState<Billing[]>([]); 
  const [selectedBilling, setselectedBilling] = useState<Billing | null>(null);
  const [doctorId, setDoctorId] = useState<number>(1);
  const [doctorOptions, setDoctorOptions] = Hooks.Form.Options();
  const [statusOptions, setStatusOptions] = Hooks.Form.Options();
  const [statusId, setStatusId] = useState<number>(Code.Option.Common.ALL);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const triggers = [pno, pageRows, search, activeId, doctorId, statusId, startDate, endDate];
  const Privilege = Hooks.Privilege.List(formId);
  const PriClient = Hooks.Privilege.List(Code.Form.Id.CLIENT);
  const locationId = Utility.Storage.LocationId();  
  
  Hooks.Privilege.HasView(Privilege);
  
  Form.Click.Outside([contextMenuRef, filterRef],[() => setShowMenu(false),() => {}],true);
  Form.Select.OnChange(selectedItems, setSelected);

  useEffect(() => {
    Files.Json.API(Code.Form.Name.DOCTOR, Code.Option.Common.ALL, setDoctorOptions, setError);
    Files.Json.Http(Code.File.ACTIVE, Code.Option.Common.NOTHING, setActiveOptions, setError);
    Files.Json.Http(Code.File.ROW, Code.Option.Common.NOTHING, setRowOptions, setError);
    Files.Json.Http(Code.File.CLAIM+Code.File.STATUS, Code.Option.Common.ALL, setStatusOptions, setError);
  }, []);

  useEffect(() => {
    callListData(); 
  }, triggers);
 
  const callListData = useCallback(async () => {
    const url = API.Build.ListApiEndpoint(formName, pno, pageRows, Name.Billing.Id, Code.Option.Sort.DESC, activeId, search, {
      fields: Utility.Join.Fields([
        Name.Billing.Id, 
        Name.Billing.Number, 
        Name.Billing.Amount, 
        Name.Billing.InsuranceAmount, 
        Name.Billing.HscName,
        Name.Billing.Diagnosis1Name,
        Name.Client.Id, 
        Name.Client.FullName, 
        Name.Client.HealthCard, 
        Name.Common.StatusName, 
        Name.Billing.ExplanatoryCode, 
        Name.Doctor.FullName, 
        Name.User.FullName, 
        Name.Billing.BillingDate, 
        Name.Billing.ClaimDate, 
        Name.Billing.RemittanceDate, 
        Name.Submitter.Prefix,
      ]),
      ...(!Privilege.ALLLOC && { location_id: locationId }),
      ...(doctorId != 1 && { doctor_id: doctorId }),
      ...(statusId != 1 && { status_id: statusId }),
      ...(startDate != '' && { start_date: startDate }),
      ...(endDate != '' && { end_date: endDate }),
    });

    await API.Data.List<Billing>(url, setBillingData, setError, setLoading); 
  }, triggers);

  

  return (
    <Control.List.Frame>
      <Control.List.Panel>
        <Control.List.Tools>
          <Control.Tool.Left>
            <Button.Create title={formName} onClick={() => Form.Show.Popup(iframeRef, formName)} Privilege={Privilege} />
            <Button.Create title={"Multiple"} onClick={() => Form.Show.Popup(iframeRef, "multibilling")} Privilege={Privilege} />
            {selected && ( <Button.Delete onClick={()=>Toast.Delete.MultipleRecords(activeId, selectedItems, setConfirm)} activeId={activeId} Privilege={Privilege} /> )}
          </Control.Tool.Left>
          <Control.Tool.Right>
            <Control.SearchBar onSearch={setSearch} onFilterToggle={() => Form.Show.Filter(filterRef)}  onGoClick={() => callListData()} />
            <Control.List.Filter filterRef={filterRef}>
              <ControlSession startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate} />
              <Control.Dropdown title={Title.Doctor} value={doctorId} options={doctorOptions} onChange={(value) => { setDoctorId(value);  callListData();  }} />
              <Control.Dropdown title={Title.Status} value={statusId} options={statusOptions} onChange={setStatusId} />
              <Control.Dropdown title={Title.State} value={activeId} options={activeOptions} onChange={setActiveId} />
              <Control.Dropdown title={Title.Rows} value={pageRows} options={rowOptions} onChange={setPageRow} />
            </Control.List.Filter>
            <Messages.Confirm Messages={confirm} onConfirm={() => API.Data.MultiDelete(formName, activeId, callListData, selectedItems, setConfirm, setMessage, setSelected, setSelectedItems, setError) } onRefuse={() => setSelectedItems([])} />
          </Control.Tool.Right>
        </Control.List.Tools>
        <Control.List.Data>
          <Control.List.Box>
            <Control.List.Title>
              <Control.SelectAll selectAllChecked={selectAllChecked} onSelectAll={(e) => Form.Select.All(e, setSelectedItems, billingData, setSelectAllChecked,formName)} extra={1} />
              <Control.Grid.Items values={[
                [Title.SNo, width.sno],
                [Title.ClientName, width.client], 
                [Title.Amount, width.amount, Code.Align.RIGHT], 
                [Title.Paid, width.paid, Code.Align.RIGHT],
                [Title.HSC, width.hsc], 
                [Title.Diag, width.diag],
                [Title.Status, width.status], 
                [Title.Doctor, width.doctor], 
                [Title.Billed, width.billdate],
                [Title.Claimed, width.claimdate],
                [Title.Remittance, width.remitdate],
              ]} last={1} upper={true} />
            </Control.List.Title>
            {!loading && !error && Array.isArray(billingData) && (
              <Control.Grid.Row>
                {billingData.map((billingData) => { return (
                <Control.Grid.Data<Billing> key={billingData.billing_id} itemKey={billingData.billing_key} data={billingData} setMenuPosition={setMenuPosition} setSelectedData={setselectedBilling} setShowMenu={setShowMenu} highlight={billingData.client_health_card?.trim().length !== 9} title={'Health Card missing or invalid'} >
                  <Control.Grid.Check keyId={billingData.billing_key} selectedItems={selectedItems} toggleSelection={Form.Select.Toggle} setSelectedItems={setSelectedItems} />
                  <Control.Grid.Modify path={Utility.Join.Edit(Privilege, formName, billingData.billing_key)} iframeRef={iframeRef} Privilege={Privilege} />
                  <Control.Grid.Text value={billingData.billing_number} width={width.sno} />
                  <Control.Grid.Link value={Utility.Get.FullName(billingData,Code.Form.Name.CLIENT)} path={Utility.Join.Edit(PriClient, Code.Form.Name.CLIENT, billingData.client_id)} width={width.client} iframeRef={iframeRef} />
                  <Control.Grid.Items values={[
                    [Utility.Format.Amount(billingData.billing_amount), width.amount, Code.Align.RIGHT],
                    [Utility.Format.Amount(billingData.billing_insurance_amount), width.paid, Code.Align.RIGHT],
                    [billingData.billing_hsc_name, width.hsc],
                    [billingData.billing_diagnosis1_name, width.diag],
                    [`${billingData.status_name} ${billingData.billing_explanatory_code}`, width.status],
                    [billingData.doctor_first_name, width.doctor],
                    [Utility.Format.DateOnly(billingData.billing_date), width.billdate],
                    [Utility.Format.DateOnly(billingData.claim_date), width.claimdate],
                    [Utility.Format.DateOnly(billingData.remittance_date), width.remitdate],
                  ]} />
                  <Control.Grid.Actions>
                    <Control.Action.All formName={formName} activeId={activeId} keyId={billingData.billing_key} iframeRef={iframeRef} setSelectedItems={setSelectedItems} setConfirm={setConfirm} Privilege={Privilege}><></></Control.Action.All>
                  </Control.Grid.Actions>
                </Control.Grid.Data>)})}
            </Control.Grid.Row>)}
          </Control.List.Box>
        </Control.List.Data>
        <Control.List.Info>
          <Control.RecordInfo pageNo={pno} pageRows={pageRows} loading={loading} error={error} dataLength={billingData.length} />
          <Control.Pagination pageNo={pno} onChange={(page) => setPno(page)} />
        </Control.List.Info>
      </Control.List.Panel>
      {showMenu && (<div ref={contextMenuRef}><Control.ListContextMenu showMenu={showMenu} formName={formName} onCreate={() => Form.Show.Popup(iframeRef, formName)} onModify={()=>Form.Show.Popup(iframeRef, Utility.Join.Edit(Privilege, formName,selectedBilling?.billing_key))} onDelete={() => Toast.Delete.SingleRecord(activeId, selectedBilling?.billing_key, setSelectedItems, setConfirm)} onClose={() => Form.Parent.TabClose()} setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} /></div>)}
      <Control.FormPopup iframeRef={iframeRef} onRefresh={callListData} />
      <Actions.List />
      <CustomTooltip />
    </Control.List.Frame>
  );
};

export default BillingList;